<template>
  <nav :class="{ 'menu-open': menuOpen, 'scrolled': scrolled }">
    <!-- Desktop left -->
    <ul class="nav-left">
      <li><router-link to="/" @click="close">Home</router-link></li>
      <li><router-link to="/about" @click="close">About</router-link></li>
      <li><router-link to="/packages" @click="close">Packages</router-link></li>
    </ul>

    <!-- Logo -->
    <router-link to="/" class="logo" @click="close">Evelina Lodi</router-link>

    <!-- Desktop right -->
    <ul class="nav-right">
      <li><router-link to="/portfolio" @click="close">Portfolio</router-link></li>
      <li><router-link to="/faq" @click="close">FAQ</router-link></li>
      <li><router-link to="/contact" class="inquire-btn" @click="close">Inquire Now</router-link></li>
    </ul>

    <!-- Hamburger -->
    <button class="hamburger" @click="toggle" :aria-label="menuOpen ? 'Close menu' : 'Open menu'">
      <span></span>
      <span></span>
      <span></span>
    </button>

    <!-- Mobile menu overlay -->
    <div class="mobile-menu" :class="{ open: menuOpen }">
      <ul>
        <li><router-link to="/" @click="close">Home</router-link></li>
        <li><router-link to="/about" @click="close">About</router-link></li>
        <li><router-link to="/portfolio" @click="close">Portfolio</router-link></li>
        <li><router-link to="/packages" @click="close">Packages</router-link></li>
        <li><router-link to="/faq" @click="close">FAQ</router-link></li>
        <li><router-link to="/contact" @click="close" class="mobile-inquire">Inquire Now</router-link></li>
      </ul>
    </div>
  </nav>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

export default defineComponent({
  name: "NavBar",
  setup() {
    const menuOpen = ref(false);
    const scrolled = ref(false);

    const toggle = () => { menuOpen.value = !menuOpen.value; };
    const close = () => { menuOpen.value = false; };

    const onScroll = () => { scrolled.value = window.scrollY > 60; };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    return { menuOpen, scrolled, toggle, close };
  },
});
</script>

<style lang="scss" scoped>
nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 2rem 5%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  background: var(--cream);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 3.5rem;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.5s ease;
    background: linear-gradient(
      to bottom,
      var(--cream) 0%,
      rgba(250, 248, 245, 0.7) 30%,
      rgba(250, 248, 245, 0.3) 60%,
      transparent 100%
    );
  }

  &.scrolled::after {
    opacity: 1;
  }
}

.nav-left,
.nav-right {
  display: flex;
  gap: clamp(0.8rem, 1.5vw, 2rem);
  list-style: none;
  align-items: center;
}

.nav-left { justify-content: flex-start; flex: 1; }
.nav-right { justify-content: flex-end; flex: 1; }

.nav-left a,
.nav-right a {
  color: var(--charcoal);
  text-decoration: none;
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 500;
  transition: opacity 0.3s ease;

  &:hover { opacity: 0.6; }
}

.logo {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  font-weight: 400;
  color: var(--charcoal);
  text-decoration: none;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.inquire-btn {
  background: var(--charcoal);
  color: var(--cream) !important;
  padding: 0.55rem 1.4rem;
  border-radius: 50px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--grey);
    transform: translateY(-2px);
    opacity: 1 !important;
  }
}

/* Hamburger — hidden on desktop */
.hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  z-index: 1100;

  span {
    display: block;
    width: 24px;
    height: 1.5px;
    background: var(--charcoal);
    transition: all 0.3s ease;
    transform-origin: center;
  }
}

/* Hamburger → X when open */
nav.menu-open .hamburger span:nth-child(1) {
  transform: translateY(6.5px) rotate(45deg);
}
nav.menu-open .hamburger span:nth-child(2) {
  opacity: 0;
}
nav.menu-open .hamburger span:nth-child(3) {
  transform: translateY(-6.5px) rotate(-45deg);
}

/* Mobile overlay menu */
.mobile-menu {
  display: none;
  position: fixed;
  inset: 0;
  background: var(--cream);
  z-index: 999;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;

  &.open {
    opacity: 1;
    pointer-events: all;
  }

  ul {
    list-style: none;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  a {
    font-family: 'Playfair Display', serif;
    font-size: clamp(1.5rem, 6vw, 2.5rem);
    font-weight: 400;
    color: var(--charcoal);
    text-decoration: none;
    letter-spacing: 0.08em;
    transition: opacity 0.3s ease;

    &:hover { opacity: 0.5; }
  }

  .mobile-inquire {
    font-family: 'Libre Franklin', sans-serif;
    font-size: 0.75rem;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    font-weight: 600;
    border-bottom: 2px solid var(--charcoal);
    padding-bottom: 0.3rem;
  }
}

/* Medium screens — tighten up before hamburger */
@media (max-width: 1280px) {
  nav {
    padding: 1.5rem 3%;
  }

  .nav-left a,
  .nav-right a {
    font-size: 0.68rem;
    letter-spacing: 0.1em;
  }

  .logo {
    font-size: 1.15rem;
    letter-spacing: 0.2em;
  }

  .inquire-btn {
    padding: 0.45rem 1rem;
  }
}

/* Mobile — hamburger */
@media (max-width: 1024px) {
  nav {
    padding: 1.5rem 5%;
    background: var(--cream);
  }

  .nav-left,
  .nav-right {
    display: none;
  }

  .logo {
    position: static;
    transform: none;
    font-size: 1.1rem;
  }

  nav::after {
    display: none;
  }

  .hamburger {
    display: flex;
  }

  .mobile-menu {
    display: flex;
  }
}
</style>
